import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function GorillaTrekking() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    {
      question: "How difficult is gorilla trekking really?",
      answer: "Let's be honest—it can be challenging. Treks range from 2-8 hours over steep, muddy, slippery terrain through dense vegetation at altitude (1,160 to 2,706 meters). But here's what helps: rangers can assign you to closer gorilla families if you express fitness concerns during the briefing, and porters are available to help with your gear and provide physical support on difficult sections. If you can climb several flights of stairs without getting winded, you've got this. The reward makes every challenging step worthwhile."
    },
    {
      question: "Will I definitely see gorillas?",
      answer: "Success rates exceed 98%! Rangers track gorilla families every morning before tourists arrive, so they know exactly where they are. In the extremely rare case you don't find them (it's happened maybe twice in the past decade), we work with you on next steps—this is why booking through a reputable operator matters. In our experience guiding hundreds of treks, we've never had a client not see gorillas."
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Gorilla Trekking in Bwindi Impenetrable – 2025/2026
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Meet the gentle giants in their ancient rainforest home
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(500+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                UWA Licensed
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                Carbon Neutral
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                Community Supported
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Adventure
            </button>
          </div>
        </div>
      </section>
      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Full Activity Description */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Your Gorilla Encounter Awaits
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Picture this: you're standing just meters away from a 200-kilogram silverback gorilla as he peacefully munches on bamboo shoots,
                  his deep brown eyes meeting yours with unmistakable intelligence. This isn't a scene from a nature documentary—this is what
                  you'll experience when you trek through the misty rainforests of Bwindi Impenetrable National Park.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Here's what makes this special: Bwindi's dense canopy shelters approximately 460 mountain gorillas—nearly half of the world's
                  remaining population. You'll be joining a small, carefully managed group to spend time with one of the 25 habituated gorilla
                  families. These gentle giants share 98% of our DNA, and watching them interact, play, and go about their daily lives creates
                  moments that genuinely transform how you see the natural world.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">A Conservation Success Story</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The mountain gorilla population was once critically endangered, down to fewer than 650 individuals worldwide.
                  Today, their numbers are slowly recovering thanks to intensive conservation efforts and responsible tourism. Your trekking
                  permit fee goes directly toward anti-poaching patrols, habitat protection, and community development programs that give
                  local people a stake in conservation. This means you're not just observing wildlife—you're actively contributing to the
                  protection of these incredible animals.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">What Makes Bwindi Special</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Bwindi Impenetrable National Park isn't called "impenetrable" for nothing. This UNESCO World Heritage Site encompasses
                  331 square kilometers of ancient montane and lowland forest that dates back over 25,000 years—one of Africa's oldest
                  ecosystems. The forest stretches across elevations from 1,160 to 2,706 meters, creating diverse habitats that support
                  not just gorillas, but over 120 mammal species, 350 bird species, and 400+ types of plants.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The park operates across four sectors—Buhoma, Rushaga, Ruhija, and Nkuringo—each offering unique trekking experiences with
                  different terrain and difficulty levels. With 25 habituated families to visit, success rates exceed 98%. Rangers track
                  the gorillas every morning before tourists arrive, so your guides know exactly where to find them. This means your encounter
                  is virtually guaranteed (we've never had a failed sighting!).
                </p>
              </div>
            </section>

            {/* Activity Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                What Makes This Experience Unforgettable
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-heart" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Intimate Wildlife Encounter</h3>
                    <p className="text-gray-700">Spend one magical hour just meters from mountain gorillas in their natural habitat</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-percent" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">95%+ Success Rate</h3>
                    <p className="text-gray-700">Rangers track gorilla families daily, virtually guaranteeing your encounter</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-tree" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Ancient Rainforest Trek</h3>
                    <p className="text-gray-700">Explore a UNESCO World Heritage forest dating back 25,000 years</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Small Group Experience</h3>
                    <p className="text-gray-700">Maximum 8 people per gorilla family for an intimate, non-intrusive visit</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Sample Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Day in Detail – Sample Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be fully customized based on your preferences,
                  pickup location, and which gorilla family you're assigned to. We can adjust timing, combine with other activities,
                  or modify any aspect to suit your needs. Just let us know what works best for you!
                </p>
              </div>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Typically, your gorilla trekking day unfolds like this when you're already staying near Bwindi:
              </p>

              <div className="space-y-6">
                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        06:00
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Early Morning Wake-Up</h3>
                      <p className="text-gray-700">
                        Start your day with a hearty breakfast at your lodge. You'll need the energy! Dress in layers with long pants and sleeves,
                        a waterproof jacket, and sturdy hiking boots. Don't forget gardening gloves (they really help when grabbing vines for support).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        07:30
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Briefing at Park Headquarters</h3>
                      <p className="text-gray-700">
                        Arrive at the park headquarters for registration and orientation. Rangers will explain gorilla viewing etiquette, safety
                        protocols, and assign you to a specific gorilla family based on your fitness level. This is when you can hire a porter
                        (optional but highly recommended—they carry your daypack and help you navigate tricky sections, plus hiring them directly
                        supports local communities).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        08:30
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Trek Begins</h3>
                      <p className="text-gray-700">
                        Your guide leads you into the dense forest, following trails that quickly disappear into thick vegetation. The trek duration
                        varies widely—anywhere from 30 minutes to 8 hours—depending on where the gorillas spent the night (they build new nests daily).
                        You'll climb steep slopes, cross streams, and push through vines, but the anticipation and your guide's stories keep you going.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Your Questions Answered" />

            {/* Guest Reviews */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                What Our Guests Say
              </h2>

              <div className="grid md:grid-cols-1 gap-8">
                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-safari-green flex items-center justify-center text-white text-2xl font-bold">
                        SJ
                      </div>
                      <div>
                        <h4 className="font-bold text-safari-green">Sarah Johnson</h4>
                        <p className="text-sm text-gray-600">Seattle, USA • August 2024</p>
                        <div className="flex text-safari-gold mt-1">
                          {[...Array(5)].map((_, i) => (
                            <FontAwesomeIcon key={i} icon="fa-star" className="w-4 h-4" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 italic mb-4">
                      "Hands down the most incredible experience of my life. When that massive silverback locked eyes with me, time stopped.
                      The trek was challenging—I won't lie—but our guide made sure everyone felt safe and supported. Hiring a porter was the
                      best decision ever. The permit fee is substantial, but this experience? Absolutely priceless. Do this before you die!"
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* More Adventures */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                More Adventures You'll Love
              </h2>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Combine gorilla trekking with these amazing experiences:
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-habituation" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800"
                      alt="Gorilla Habituation"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Habituation Experience</h3>
                    <p className="text-gray-700 mb-4">Spend 4 hours with researchers training semi-habituated gorillas. The ultimate encounter!</p>
                    <p className="text-safari-gold font-bold">Permit fees apply – contact us for details</p>
                  </div>
                </Link>

                <Link to="/activities/batwa-cultural-trail" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800"
                      alt="Batwa Trail"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Batwa Cultural Trail</h3>
                    <p className="text-gray-700 mb-4">Walk with the forest's original inhabitants and learn their ancient survival skills.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 2-8 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Permit Fee:</strong> Required (contact for current rates)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate to Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 8 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Gorilla trekking permit (mandatory)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Experienced park ranger guide</span>
                  </li>
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Porter hire (optional but recommended—supports local communities)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Accommodation before and after the trek</span>
                  </li>
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness Level:</strong> Moderate to high fitness recommended.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Medical Requirements:</strong> Yellow fever vaccination mandatory.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Gorilla Trekking - Bwindi Impenetrable"
      />
    </div>
  )
}
